import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { useCanvasSignature } from '../hooks/useCanvasSignature';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeliveryModal = ({ order, onClose, onDelivered }) => {
  const [formData, setFormData] = useState({
    received_by_name: '',
    observations: '',
    signature: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const {
    canvasRef,
    handlers: signatureHandlers,
    clear: clearSignature,
    isEmpty: isSignatureEmpty,
    toDataURL: signatureToDataURL,
  } = useCanvasSignature();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.received_by_name) {
      toast.error('El nombre de quien recibe es requerido');
      return;
    }
    if (isSignatureEmpty()) {
      toast.error('La firma es requerida');
      return;
    }

    const signatureData = signatureToDataURL('image/png');

    setSubmitting(true);
    try {
      await axios.put(`${API}/orders/${order.id}/deliver`, {
        received_by_name: formData.received_by_name,
        observations: formData.observations || null,
        signature: signatureData
      });

      toast.success('Orden marcada como entregada');
      onDelivered();
      onClose();
    } catch (error) {
      logger.error('Error delivering order:', error);
      toast.error('Error al marcar como entregada');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Formato de Entrega
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary"
            data-testid="close-delivery-modal"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="p-4 bg-secondary border border-border">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Orden
            </p>
            <p className="text-lg font-bold font-mono mt-1">{order.order_number}</p>
            <p className="text-sm text-muted-foreground mt-1">{order.client_name}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="received_by_name" className="text-xs font-bold uppercase tracking-[0.2em]">
              Nombre de quien Recibe *
            </Label>
            <Input
              id="received_by_name"
              value={formData.received_by_name}
              onChange={(e) => setFormData({ ...formData, received_by_name: e.target.value })}
              required
              data-testid="received-by-name-input"
              className="border-border"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="observations" className="text-xs font-bold uppercase tracking-[0.2em]">
              Observaciones
            </Label>
            <Textarea
              id="observations"
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              data-testid="observations-input"
              className="border-border min-h-[100px]"
              placeholder="Comentarios adicionales sobre la entrega..."
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">
              Firma del Receptor *
            </Label>
            <div className="border-2 border-border bg-white">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                {...signatureHandlers}
                className="w-full cursor-crosshair touch-none select-none"
                data-testid="signature-canvas"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSignature}
              data-testid="clear-signature-button"
              className="hover:bg-secondary"
            >
              Limpiar Firma
            </Button>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-delivery-button"
              className="hover:bg-secondary"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-delivery-button"
              className="bg-primary hover:bg-primary/90"
            >
              {submitting ? 'Procesando...' : 'Marcar como Entregada'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
